import React, { useEffect, useRef } from "react";
import { StaticQuery, Link, graphql } from "gatsby";
import { Container, Nav, NavItem, NavLink, Row, Col, Button } from "reactstrap";

import { addresses } from "../../../utils/variables";
import { resolveInternalRedirect } from "../../../utils/functions";

import iconYoutube from "../../../img/icons/picto-youtube.svg";
import iconPinterest from "../../../img/icons/picto-pinterest.svg";
import iconTiktok from "../../../img/icons/picto-tiktok.svg";
import iconSnapchat from "../../../img/icons/picto-snapchat.svg";
import iconLinkedin from "../../../img/icons/linked-in.svg";
import iconThreads from "../../../img/icons/picto-threads.svg";

import i18n from "./../../../i18n/i18n.json";

import LanguageContext from "./../../../state/context/LanguageContext";

const Menu = ({ openned, hidden, toggleMenu, hideMenu }) => {
	// Validation des props
	if (typeof openned !== 'boolean' || typeof hidden !== 'boolean' || typeof toggleMenu !== 'function' || typeof hideMenu !== 'function') {
		return (
			<div 
				className="menu-mobile-error"
				role="alert"
				aria-live="polite"
				aria-describedby="menu-error-description"
			>
				<div id="menu-error-description" className="visually-hidden" data-testid="menu-error-description">
					Erreur d'affichage du menu mobile
				</div>
				<p className="text-danger" data-testid="menu-error-message">Erreur d'affichage du menu mobile</p>
			</div>
		)
	}

	const menuRef = useRef(null);
	const closeButtonRef = useRef(null);
	const closeButtonId = 'mobile-menu-close-button';

	// Gestion du focus et de la navigation clavier
	useEffect(() => {
		if (openned && !hidden) {
			// Utiliser setTimeout pour s'assurer que le DOM est rendu
			setTimeout(() => {
				// Accéder à l'élément DOM via l'ID car reactstrap Button ne transmet pas directement la ref au DOM
				const closeButton = document.getElementById(closeButtonId);
				if (closeButton && typeof closeButton.focus === 'function') {
					closeButton.focus();
				} else if (closeButtonRef.current) {
					// Fallback : essayer d'accéder via innerRef si disponible
					const buttonElement = closeButtonRef.current.querySelector?.('button') || 
					                     closeButtonRef.current.getDOMNode?.() ||
					                     closeButtonRef.current;
					if (buttonElement && typeof buttonElement.focus === 'function') {
						buttonElement.focus();
					}
				}
			}, 0);
		}
	}, [openned, hidden]);

	// Gestion de la touche Escape
	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === 'Escape' && openned && !hidden) {
				hideMenu();
			}
		};

		if (openned && !hidden) {
			document.addEventListener('keydown', handleKeyDown);
			return () => document.removeEventListener('keydown', handleKeyDown);
		}
	}, [openned, hidden, hideMenu]);

	// Gestion du focus trap
	useEffect(() => {
		if (openned && !hidden && menuRef.current) {
			const focusableElements = menuRef.current.querySelectorAll(
				'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
			);
			
			if (focusableElements.length > 0) {
				const firstElement = focusableElements[0];
				const lastElement = focusableElements[focusableElements.length - 1];

				const handleTabKey = (event) => {
					if (event.key === 'Tab') {
						if (event.shiftKey) {
							if (document.activeElement === firstElement) {
								event.preventDefault();
								lastElement.focus();
							}
						} else {
							if (document.activeElement === lastElement) {
								event.preventDefault();
								firstElement.focus();
							}
						}
					}
				};

				menuRef.current.addEventListener('keydown', handleTabKey);
				return () => menuRef.current?.removeEventListener('keydown', handleTabKey);
			}
		}
	}, [openned, hidden]);

	const languageSlugs = Object.keys(i18n).map(l => i18n[l].slug);
	const menuId = 'mobile-menu-navigation';

	return (
		<LanguageContext.Consumer>
			{({ language, translation }) => (
				<div
					ref={menuRef}
					className={`menu-mobile-0 py-5${hidden ? " hidden" : ""}${openned ? " openned" : " closed"}`}
					role="dialog"
					aria-modal="true"
					aria-labelledby={menuId}
					aria-describedby="mobile-menu-description"
					aria-hidden={hidden || !openned}
				>
					<div id="mobile-menu-description" className="visually-hidden">
						Menu de navigation mobile principal avec liens vers les pages du site et réseaux sociaux
					</div>
					<Container>
						<Row>
							<Col>
								<Button 
									ref={closeButtonRef}
									id={closeButtonId}
									className="menu-close ml-auto" 
									onClick={toggleMenu}
									aria-label="Fermer le menu mobile"
									aria-expanded={openned}
									aria-controls={menuId}
								>
									<span className="visually-hidden">Fermer</span>
								</Button>
							</Col>
						</Row>
						<Nav 
							id={menuId}
							className="mr-auto flex-shrink-0 main" 
							navbar
							role="navigation"
							aria-label="Navigation principale"
						>
							<StaticQuery
								query={graphql`
									query {
										menu: allWordpressPage(
											filter: { wordpress_parent: { eq: 0 } }
										) {
											edges {
												node {
													title
													full_slug
													wordpress_id
													menu_order
													hide_menu
													language
												}
											}
										}
									}
								`}
								render={({ menu }) => {
									if (!menu || !menu.edges) {
										return (
											<NavItem>
												<span className="nav-link py-3 text-muted">
													{translation?.menuError || 'Erreur de chargement du menu'}
												</span>
											</NavItem>
										);
									}

									const isHiddenFromMenu = (val) =>
										val === true || val === 1 || val === '1' || (typeof val === 'string' && val.toLowerCase() === 'true')
									const getHideMenu = (node) => node.hide_menu !== undefined ? node.hide_menu : node.hideMenu
									const filteredMenu = menu.edges
										.filter(
											({ node }) =>
												node &&
												!isHiddenFromMenu(getHideMenu(node)) &&
												node.language === language &&
												!languageSlugs.includes(node.full_slug?.slice(1, -1) || '')
										)
										.sort((a, b) => (a.node.menu_order || 0) - (b.node.menu_order || 0));

									if (filteredMenu.length === 0) {
										return (
											<NavItem>
												<span className="nav-link py-3 text-muted">
													{translation?.noMenuItems || 'Aucun élément de menu disponible'}
												</span>
											</NavItem>
										);
									}

									return filteredMenu.map(({ node }) => (
										<NavItem key={node.wordpress_id}>
											<Link
												className="nav-link py-3"
												to={resolveInternalRedirect(node.full_slug) || '/'}
												dangerouslySetInnerHTML={{ __html: node.title || 'Sans titre' }}
												onClick={hideMenu}
												aria-label={`Aller à la page : ${node.title || 'Sans titre'}`}
											/>
										</NavItem>
									));
								}}
							/>
							<NavItem>
								<Link
									className="nav-link py-3"
									to={addresses.roubaixdemarche || '#'}
									target="_blank"
									rel="noopener noreferrer"
									onClick={hideMenu}
									aria-label={`${translation?.demarchelittle || 'Démarche'} (s'ouvre dans un nouvel onglet)`}
								>
									{translation?.demarchelittle || 'Démarche'}
								</Link>
							</NavItem>
						</Nav>

						<Nav
							navbar
							className="social mt-4"
							role="navigation"
							aria-label="Réseaux sociaux"
						>
							<NavItem className="align-items-center">
								<NavLink
									href={addresses.facebook || '#'}
									className="icon-link d-block text-center text-dark rounded-circle mr-2 p-0"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Facebook (s'ouvre dans un nouvel onglet)"
								>
									<i className="icon-facebook" aria-hidden="true" />
									<span className="visually-hidden">Facebook</span>
								</NavLink>
							</NavItem>
							<NavItem className="align-items-center">
								<NavLink
									href={addresses.twitter || '#'}
									className="icon-link d-block text-center text-dark rounded-circle mr-2 p-0"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Twitter (s'ouvre dans un nouvel onglet)"
								>
									<i className="icon-twitter" aria-hidden="true" />
									<span className="visually-hidden">Twitter</span>
								</NavLink>
							</NavItem>
							<NavItem className="align-items-center">
								<NavLink
									href={addresses.instagram || '#'}
									className="icon-link d-block text-center text-dark rounded-circle mr-2 p-0"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Instagram (s'ouvre dans un nouvel onglet)"
								>
									<i className="icon-instagram" aria-hidden="true" />
									<span className="visually-hidden">Instagram</span>
								</NavLink>
							</NavItem>
							<NavItem className="align-items-center">
								<NavLink
									href={addresses.youtube || '#'}
									className="icon-link icon-link-img rounded-circle mr-2 p-0"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="YouTube (s'ouvre dans un nouvel onglet)"
								>
									<img src={iconYoutube} alt="" aria-hidden="true" />
									<span className="visually-hidden">YouTube</span>
								</NavLink>
							</NavItem>
							<NavItem className="align-items-center">
								<NavLink
									href={addresses.pinterest || '#'}
									className="icon-link icon-link-img rounded-circle mr-2 p-0"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Pinterest (s'ouvre dans un nouvel onglet)"
								>
									<img src={iconPinterest} alt="" aria-hidden="true" />
									<span className="visually-hidden">Pinterest</span>
								</NavLink>
							</NavItem>
							<NavItem className="align-items-center">
								<NavLink
									href={addresses.tiktok || '#'}
									className="icon-link icon-link-img rounded-circle mr-2 p-0"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="TikTok (s'ouvre dans un nouvel onglet)"
								>
									<img src={iconTiktok} alt="" aria-hidden="true" />
									<span className="visually-hidden">TikTok</span>
								</NavLink>
							</NavItem>
							<NavItem className="align-items-center">
								<NavLink
									href={addresses.snapchat || '#'}
									className="icon-link icon-link-img rounded-circle mr-2 p-0"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Snapchat (s'ouvre dans un nouvel onglet)"
								>
									<img src={iconSnapchat} alt="" aria-hidden="true" />
									<span className="visually-hidden">Snapchat</span>
								</NavLink>
							</NavItem>
							<NavItem className="align-items-center">
								<NavLink
									href={addresses.linkedin || '#'}
									className="icon-link icon-link-img rounded-circle mr-2 p-0"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="LinkedIn (s'ouvre dans un nouvel onglet)"
								>
									<img src={iconLinkedin} alt="" aria-hidden="true" />
									<span className="visually-hidden">LinkedIn</span>
								</NavLink>
							</NavItem>
							<NavItem className="align-items-center">
								<NavLink
									href={addresses.threads || '#'}
									className="icon-link icon-link-img rounded-circle mr-2 p-0"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Threads (s'ouvre dans un nouvel onglet)"
								>
									<img src={iconThreads} alt="" aria-hidden="true" />
									<span className="visually-hidden">Threads</span>
								</NavLink>
							</NavItem>
						</Nav>

						<Nav
							navbar
							className="links mt-5"
							role="navigation"
							aria-label="Tous les sites de la ville"
						>
							<NavItem>
								<NavLink
									target="_blank"
									rel="noopener noreferrer"
									href={addresses.roubaixxl || '#'}
									className="pl-5 py-4 logo-roubaixxl d-block"
									aria-label={`${translation?.roubaixxl || 'Actualités'} (s'ouvre dans un nouvel onglet)`}
								>
									{translation?.roubaixxl || 'Actualités'}
								</NavLink>
							</NavItem>
							<NavItem>
								<NavLink
									target="_blank"
									rel="noopener noreferrer"
									href={addresses.roubaixdemarche || '#'}
									className="pl-5 py-4 logo-demarche d-block"
									aria-label={`${translation?.demarche || 'Démarches en ligne'} (s'ouvre dans un nouvel onglet)`}
								>
									{translation?.demarche || 'Démarches en ligne'}
								</NavLink>
							</NavItem>
							<NavItem>
								<NavLink
									href={addresses.roubaixviva || '#'}
									className="pl-5 py-4 logo-vivacite d-block"
									onClick={hideMenu}
									aria-label={translation?.vivacite || "Centre d'appel Vivacité"}
								>
									{translation?.vivacite || "Centre d'appel Vivacité"}
								</NavLink>
							</NavItem>
							<NavItem>
								<NavLink
									target="_blank"
									rel="noopener noreferrer"
									href={addresses.lafabrique || '#'}
									className="pl-5 py-4 logo-lafabrique d-block"
									aria-label={`${translation?.lafabrique || 'Participation citoyenne'} (s'ouvre dans un nouvel onglet)`}
								>
									{translation?.lafabrique || 'Participation citoyenne'}
								</NavLink>
							</NavItem>
							<NavItem>
								<NavLink
									target="_blank"
									rel="noopener noreferrer"
									href={addresses.roubaixcarte || '#'}
									className="pl-5 py-4 logo-map d-block"
									aria-label={`${translation?.carte || 'Carte interactive'} (s'ouvre dans un nouvel onglet)`}
								>
									{translation?.carte || 'Carte interactive'}
								</NavLink>
							</NavItem>
							<NavItem>
								<Link
									to="liens"
									className="logo-fournisseur d-block nav-link"
									onClick={hideMenu}
									aria-label={`${translation?.alllinks || 'Les sites thématiques'}`}
								>
									{translation?.alllinks || 'Les sites thématiques'}
								</Link>
							</NavItem>
							<NavItem>
								<NavLink
									target="_blank"
									rel="noopener noreferrer"
									href={addresses.roubaixemploi || '#'}
									className="logo-emploi d-block"
									aria-label={`${translation?.jobs || 'Recrutement Mairie'} (s'ouvre dans un nouvel onglet)`}
								>
									{translation?.jobs || 'Recrutement Mairie'}
								</NavLink>
							</NavItem>
							<NavItem>
								<NavLink
									target="_blank"
									rel="noopener noreferrer"
									href={addresses.roubaixfournisseur || '#'}
									className="logo-fournisseur d-block"
									aria-label={`${translation?.providers || 'Recrutement CCAS'} (s'ouvre dans un nouvel onglet)`}
								>
									{translation?.providers || 'Recrutement CCAS'}
								</NavLink>
							</NavItem>
						</Nav>
					</Container>
				</div>
			)}
		</LanguageContext.Consumer>
	);
};

Menu.contextType = LanguageContext;
export default Menu;
